import React from 'react';
import { Input, Button } from '../../ui';
import { Plus, Trash2, Info } from 'lucide-react';

interface SendCarouselConfigProps {
  config: any;
  onChange: (key: string, value: any) => void;
}

export const SendCarouselConfig: React.FC<SendCarouselConfigProps> = ({ config, onChange }) => {
  return (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-700 leading-relaxed">
        <strong>Como funciona:</strong> Envia um carrossel de imagens (várias imagens em sequência) para o usuário. Cada imagem pode ter um título, descrição e link. Ideal para catálogos de produtos, portfólios ou apresentações visuais.
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Imagens do Carrossel
        </label>
        <div className="space-y-3">
          {(config.items || []).map((item: any, index: number) => (
            <div key={index} className="flex gap-2 items-center">
              <div className="flex items-center justify-center w-8 h-8 bg-slate-100 rounded text-slate-500 text-sm font-medium flex-shrink-0">
                {index + 1}
              </div>
              <Input
                placeholder="URL pública da imagem (https://...)"
                value={item.imageUrl || ''}
                onChange={(e) => {
                  const items = [...(config.items || [])];
                  items[index] = { ...items[index], imageUrl: e.target.value, title: `Imagem ${index + 1}` };
                  onChange('items', items);
                }}
              />
              {item.imageUrl && (
                <img
                  src={item.imageUrl}
                  alt={`Preview ${index + 1}`}
                  className="w-8 h-8 object-cover rounded border border-slate-200 flex-shrink-0"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
              )}
              <button
                onClick={() => {
                  const items = config.items.filter((_: any, i: number) => i !== index);
                  onChange('items', items);
                }}
                className="p-1.5 text-red-500 hover:bg-red-50 rounded flex-shrink-0"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const items = [
                ...(config.items || []),
                { id: Date.now().toString(), imageUrl: '', title: '' },
              ];
              onChange('items', items);
            }}
          >
            <Plus className="w-4 h-4 mr-1" />
            Adicionar Imagem
          </Button>
        </div>
      </div>

      {/* Explanation */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2">
        <div className="flex items-center gap-1.5 text-blue-700 font-medium text-sm">
          <Info className="w-4 h-4" />
          Como usar este nó
        </div>
        <div className="text-xs text-blue-600 space-y-1.5">
          <p>
            Este nó envia uma ou mais imagens em sequência no chat. Cada imagem precisa de uma <strong>URL pública</strong> acessível pela internet.
          </p>
          <p className="font-medium">Como obter uma URL pública:</p>
          <ul className="list-disc pl-4 space-y-0.5">
            <li><strong>Bitrix24 Drive:</strong> Faça upload do arquivo → clique com botão direito → "Obter link público" → copie o link direto da imagem.</li>
            <li><strong>Imgur / ImgBB:</strong> Faça upload em <a href="https://imgbb.com" target="_blank" rel="noopener" className="underline">imgbb.com</a> → copie o "Link direto".</li>
            <li><strong>Google Drive:</strong> Upload → Compartilhar como "Qualquer pessoa com o link" → use: <code className="bg-blue-100 px-1 rounded text-[10px]">https://drive.google.com/uc?id=SEU_ID</code></li>
            <li><strong>Servidor próprio:</strong> Qualquer URL <code className="bg-blue-100 px-1 rounded text-[10px]">https://</code> que retorne a imagem diretamente (sem redirecionamento para página HTML).</li>
          </ul>
          <p className="text-blue-500 italic">
            Formatos aceitos: JPG, PNG, GIF, WebP. A URL deve terminar em extensão de imagem ou retornar Content-Type de imagem.
          </p>
        </div>
      </div>
    </div>
  );
};
